/**
 * AdminObituaryImport — super-admin console for reviewing and
 * importing obituary listings from external partner sites (currently:
 * emalayalee.com/CHARAMAM).
 *
 * Flow:
 *   1. Admin picks a page number and region.
 *   2. Clicks "Preview" — hits GET /api/obituary-imports/emalayalee/preview.
 *   3. Reviews the parsed rows (thumbnail, name in Malayalam, age,
 *      location) and checks the ones to bring in.
 *   4. Clicks "Import selected" — hits POST /api/obituary-imports/…/import.
 *      Imported memorials appear at /celebrate/{slug} with a
 *      "Read full obituary at Emalayalee.com →" back-link.
 *
 * Any row already imported is flagged and disabled.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, ExternalLink, CheckCircle2, Download, Filter, Newspaper,
  ChevronLeft, ChevronRight, MapPin, User,
} from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Helmet } from "react-helmet-async";
import MemorialCardPrewarmPanel from "@/components/MemorialCardPrewarmPanel";

const REGION_OPTIONS = [
  { v: "kerala",    label: "Kerala, India" },
  { v: "india",     label: "India (other)" },
  { v: "us",        label: "US diaspora" },
  { v: "worldwide", label: "Worldwide" },
];

export default function AdminObituaryImport() {
  const { user, loading: authLoading } = useAuth();
  const [page, setPage] = useState(1);
  const [region, setRegion] = useState("kerala");
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [hidePhotoless, setHidePhotoless] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setSelected(new Set());
    try {
      const { data } = await http.get("/obituary-imports/emalayalee/preview", { params: { page } });
      setRows(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch preview.");
      setRows([]);
    } finally { setBusy(false); }
  }, [page]);

  useEffect(() => { if (user?.is_admin) load(); }, [load, user?.is_admin]);

  const visible = useMemo(() => {
    if (!rows) return [];
    return hidePhotoless ? rows.filter((r) => r.has_photo) : rows;
  }, [rows, hidePhotoless]);

  const importableCount = useMemo(() => visible.filter((r) => !r.already_imported).length, [visible]);

  const toggleAll = () => {
    if (selected.size >= importableCount) setSelected(new Set());
    else setSelected(new Set(visible.filter((r) => !r.already_imported).map((r) => r.source_id)));
  };
  const toggle = (sid) => {
    const s = new Set(selected);
    if (s.has(sid)) s.delete(sid); else s.add(sid);
    setSelected(s);
  };

  const doImport = async () => {
    if (selected.size === 0) return toast.error("Select at least one entry to import.");
    setImporting(true);
    try {
      const { data } = await http.post("/obituary-imports/emalayalee/import", {
        source_ids: [...selected], region, page,
      });
      toast.success(`Imported ${data.inserted}. Skipped ${data.skipped}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally { setImporting(false); }
  };

  if (authLoading) return <div className="p-10 text-center opacity-60"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!user?.is_admin) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center">
        <h1 className="font-display text-2xl">Admins only</h1>
        <p className="mt-2 opacity-70">You need super-admin access to import obituary sources.</p>
        <Link to="/" className="wofy-btn-primary mt-4 inline-flex">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-10 px-4">
      <Helmet><title>Obituary Import · Admin · Social Pulse</title></Helmet>
      <div className="mb-6">
        <div className="overline" style={{ color: "var(--wofy-primary)" }}>ADMIN · OBITUARY IMPORT</div>
        <h1 className="font-display text-3xl">Import from Emalayalee CHARAMAM</h1>
        <p className="text-sm opacity-70 mt-1 max-w-2xl">
          Pull a thumbnail, name, age and location from{" "}
          <a href="https://www.emalayalee.com/CHARAMAM" target="_blank" rel="noopener noreferrer" className="underline">
            emalayalee.com/CHARAMAM
          </a>. Every imported memorial links back to the original obituary — we never mirror the full body text.
        </p>
      </div>

      <MemorialCardPrewarmPanel />

      <div className="wofy-card mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-medium mb-1 block opacity-70">Page</label>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="wofy-btn-ghost text-xs"
              data-testid="obit-page-prev"
              disabled={page <= 1}
            >
              <ChevronLeft className="w-3 h-3" />
            </button>
            <input
              type="number" min="1" max="425" value={page}
              onChange={(e) => setPage(Math.max(1, Math.min(425, Number(e.target.value) || 1)))}
              className="wofy-input w-20 text-center"
              data-testid="obit-page-input"
            />
            <button
              onClick={() => setPage((p) => Math.min(425, p + 1))}
              className="wofy-btn-ghost text-xs"
              data-testid="obit-page-next"
            >
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block opacity-70">Import region</label>
          <select className="wofy-input" value={region} onChange={(e) => setRegion(e.target.value)} data-testid="obit-region">
            {REGION_OPTIONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
          </select>
        </div>
        <label className="text-xs flex items-center gap-1 cursor-pointer">
          <input type="checkbox" checked={hidePhotoless} onChange={(e) => setHidePhotoless(e.target.checked)} data-testid="obit-hide-photoless" />
          Only entries with a photo
        </label>
        <button onClick={load} disabled={busy} className="wofy-btn-ghost text-sm" data-testid="obit-refresh">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />} Preview
        </button>
        <div className="ml-auto flex items-center gap-2">
          <div className="text-xs opacity-70">Selected: <b>{selected.size}</b> of {importableCount}</div>
          <button onClick={toggleAll} className="wofy-btn-ghost text-xs" data-testid="obit-toggle-all">
            {selected.size >= importableCount ? "Clear" : "Select all"}
          </button>
          <button
            onClick={doImport}
            disabled={importing || selected.size === 0}
            className="wofy-btn-primary text-sm"
            data-testid="obit-import-btn"
          >
            {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <><Download className="w-4 h-4" /> Import selected</>}
          </button>
        </div>
      </div>

      {busy && rows == null && <div className="py-10 text-center opacity-60"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
      {rows && visible.length === 0 && (
        <div className="wofy-card text-sm text-center opacity-70">No entries match — try another page or uncheck the photo filter.</div>
      )}

      <div className="grid gap-2">
        {visible.map((r) => {
          const isSel = selected.has(r.source_id);
          const disabled = r.already_imported;
          return (
            <label
              key={r.source_id}
              className="wofy-card flex items-center gap-3 cursor-pointer hover:shadow-md transition"
              style={disabled ? { opacity: 0.5, cursor: "not-allowed", background: "#F9FAFB" } : {}}
              data-testid={`obit-row-${r.source_id}`}
            >
              <input
                type="checkbox"
                checked={isSel}
                disabled={disabled}
                onChange={() => !disabled && toggle(r.source_id)}
                data-testid={`obit-row-checkbox-${r.source_id}`}
              />
              <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 flex items-center justify-center">
                {r.photo_url ? (
                  <img src={r.photo_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <User className="w-6 h-6 opacity-40" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-base font-medium truncate" dir="auto">{r.name || r.title}</div>
                <div className="text-xs opacity-70 mt-0.5 flex flex-wrap items-center gap-3">
                  {r.age != null && <span>Age {r.age}</span>}
                  {r.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.location}</span>}
                  <span className="font-mono opacity-60">#{r.source_id}</span>
                </div>
              </div>
              {disabled && (
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Imported
                </span>
              )}
              <a
                href={r.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="wofy-btn-ghost text-xs"
                onClick={(e) => e.stopPropagation()}
                data-testid={`obit-row-source-${r.source_id}`}
                title="Open original article"
              >
                <ExternalLink className="w-3 h-3" />
              </a>
            </label>
          );
        })}
      </div>

      {rows && rows.length > 0 && (
        <div className="mt-6 text-xs opacity-60 flex items-center gap-1">
          <Newspaper className="w-3 h-3" />
          Content courtesy of Emalayalee.com — each imported memorial displays a "Read full obituary at Emalayalee.com →" back-link.
        </div>
      )}
    </div>
  );
}
